import { Grid, Icon, Text, Accordion, Timeline, useDate, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, ReactNode, useState } from 'react'

import { Money } from '../Money'
import { useMoney } from '../Money/useMoney'
import styles from './style.module.css'
import { CheckoutPaymentSchedule } from '@afterpaytouch/portal-api'
import { useDonation, useIsCheckoutLoadingOrUpdating, useCalculatedPaymentSchedules, useSelectedPaymentOption, useAppRouter } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'

interface ConsumerLendingPaymentScheduleProps {
  detailsFooter?: ReactNode
  planSummaryHeader?: boolean
}

export const ConsumerLendingPaymentSchedule: FunctionComponent<ConsumerLendingPaymentScheduleProps> = ({ detailsFooter, planSummaryHeader = false }) => {
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const formatDateLocale = locale === 'en-US' ? 'en-AU' : locale
  const { formatDate } = useDate({ locale: formatDateLocale, weekday: 'short', year: '2-digit' })
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { isUpdatingDonation } = useDonation()
  const isShippingOptionUpdating = useIsCheckoutLoadingOrUpdating()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const selectedPaymentOption = useSelectedPaymentOption()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { formatMoney } = useMoney({ locale, currency: selectedPaymentOption?.totalAmount.currency })

  const hasDownPayment = parseFloat(selectedPaymentOption?.amountDueToday.amount ?? '') > 0
  const paymentsCount = selectedPaymentOption?.installmentsCount ?? 0
  const recurringAmount = paymentSchedules[hasDownPayment ? 1 : 0]?.amount?.amount
  const isFortnightly = selectedPaymentOption?.paymentInterval === 'FORTNIGHTLY'
  const planTitleText = isFortnightly
    ? t('consumerLending:summary:planTitle', { number: paymentsCount, context: 'FORTNIGHTLY' })
    : t('consumerLending:summary:planTitle', { number: paymentsCount, context: 'MONTHLY' })
  const planPreviewText = isFortnightly
    ? t('consumerLending:summary:planPreview', {
        today: formatMoney(selectedPaymentOption?.amountDueToday.amount),
        recurring: formatMoney(recurringAmount),
        context: 'FORTNIGHTLY',
      })
    : t('consumerLending:summary:planPreview', {
        today: formatMoney(selectedPaymentOption?.amountDueToday.amount),
        recurring: formatMoney(recurringAmount),
        context: 'MONTHLY',
      })
  const [isExpanded, setIsExpanded] = useState(false)

  const renderTimelineItem = (paymentSchedule: CheckoutPaymentSchedule, index: number): JSX.Element => {
    const isLoading = isShippingOptionUpdating || (index === 0 && isUpdatingDonation)
    const isDownPayment = hasDownPayment && index === 0
    const isLast = index === paymentSchedules.length - 1
    const dateText = isDownPayment ? t('consumerLending:paymentSchedule:today') : formatDate(paymentSchedule.installmentDueDate)

    return (
      <Timeline.Item
        key={`paymentScheduleItem-${index}`}
        hideLine={isLast}
        highlight={index === 0}
        titleText={
          <span className={isLast ? styles.timelineItemLast : styles.timelineItem}>
            <Text size={textSize} bold renderAs='div' data-testid={`paymentScheduleItem-date-${index}`}>
              {dateText}
            </Text>
            {isDownPayment && (
              <Text color='Gray40' size={smallTextSize} renderAs='div' data-testid={`paymentScheduleItem-sequence-${index}`}>
                {t('consumerLending:summary:dueToday')}
              </Text>
            )}
          </span>
        }
        bodyText={{
          text: (
            <span data-testid={`paymentScheduleItem-money-${index}`}>
              <Money value={paymentSchedule.amount} size={textSize} loading={isLoading} bold />
            </span>
          ),
          styling: { size: textSize, bold: true },
        }}
      />
    )
  }

  const useRenderPaymentScheduleItem = (paymentSchedule: CheckoutPaymentSchedule, index: number): JSX.Element => {
    const isLoading = isShippingOptionUpdating || (index === 0 && isUpdatingDonation)
    const isDownPayment = hasDownPayment && index === 0
    const sequence = hasDownPayment ? index : index + 1
    const sequenceText = isDownPayment
      ? t('consumerLending:paymentSchedule:downPayment')
      : sequence === paymentsCount
      ? t('consumerLending:paymentSchedule:finalPayment')
      : t('consumerLending:paymentSchedule:paymentSequence', { sequence, paymentsCount })

    const dateText = isDownPayment ? t('consumerLending:paymentSchedule:today') : formatDate(paymentSchedule.installmentDueDate)

    return (
      <Grid center key={`paymentScheduleItem-${index}`}>
        <div>
          <Text size={textSize} bold data-testid={`paymentScheduleItem-date-${index}`}>
            {dateText}
          </Text>
          <Text color='Gray40' size={smallTextSize} data-testid={`paymentScheduleItem-sequence-${index}`}>
            {sequenceText}
          </Text>
        </div>
        <Grid.FixedItem width={108} align='right' data-testid={`paymentScheduleItem-money-${index}`}>
          <Money value={paymentSchedule.amount} size={textSize} loading={isLoading} />
        </Grid.FixedItem>
      </Grid>
    )
  }

  return (
    <div className={planSummaryHeader ? `${designLanguageClass.wrapper} ${styles.wrapperCompact}` : designLanguageClass.wrapper}>
      <Accordion
        id='consumerLending-payment-schedule'
        onChange={(isOpen) => {
          setIsExpanded(isOpen)
          if (isOpen) {
            logEvent(TrackingEvent.TOGGLED_CL_PAYMENT_SCHEDULE)
          }
        }}
      >
        <Accordion.Button>
          {planSummaryHeader ? (
            <div className={styles.planSummaryHeader} data-testid='consumerLending-plan-summary-header'>
              <Icon name='Calendar' />
              <div className={styles.planSummaryHeaderInfo}>
                <Text size={textSize} bold>
                  {planTitleText}
                </Text>
                <Text size={smallTextSize} color='Gray40'>
                  {planPreviewText}
                </Text>
              </div>
            </div>
          ) : (
            <Grid center>
              <Text size={textSize}>{t('common:paymentSchedule')}</Text>
              <Grid.FixedItem width={108} align='right'>
                <Text size={textSize}>{t('consumerLending:paymentsCount', { count: paymentsCount, context: selectedPaymentOption?.paymentInterval })}</Text>
              </Grid.FixedItem>
            </Grid>
          )}
        </Accordion.Button>
        <Accordion.Details>
          <div
            className={
              planSummaryHeader
                ? `${designLanguageClass.paymentSchedulesContainer} ${styles.paymentSchedulesContainerPlain}`
                : designLanguageClass.paymentSchedulesContainer
            }
          >
            {planSummaryHeader ? (
              <Timeline kind='Vertical' testNameSpace='consumerLending-payment-schedule-timeline'>
                {paymentSchedules.map(renderTimelineItem)}
              </Timeline>
            ) : (
              paymentSchedules.map(useRenderPaymentScheduleItem)
            )}
          </div>
          {isExpanded && detailsFooter}
        </Accordion.Details>
      </Accordion>
    </div>
  )
}
